import React from "react";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import moment from "moment";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { Card, Col, Form, Row } from "react-bootstrap";
import { FieldArray, Formik } from "formik";
import type { FieldArrayRenderProps, FormikHelpers } from "formik";
import { toast } from "react-toastify";
import { each, get, isEmpty, last, map, reduce, size, some } from "lodash";
import { FiUsers, FiMinus, FiPlus } from "react-icons/fi";
import * as yup from "yup";

import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { accountConstants, beneficiaryConstants } from "actions/types";
import {
  address2Validator,
  postalCodeValidator,
  testSsn,
  INVALID_SSN_ERROR,
  DOB_FORMAT_ERROR,
  testDobFormat,
  parseDob,
  stateValidator,
} from "utils/fieldValidators";
import { states } from "statics/countriesAndStates";
import { getAllAccounts } from "actions/accountActions";
import {
  getError,
  getValue,
  isInvalid,
  isValid,
  ScrollToFieldError,
} from "utils/form";
import { getIraAccountIdSelector } from "store/selectors/user";
import {
  deleteBeneficiaries,
  getBeneficiaries,
  updateBeneficiaries,
} from "actions/beneficiaryActions";

import MaskedInput from "components/MaskedInput";
import IconTable from "components/IconTable";
import type { IconTableColumn } from "components/IconTable";
import IconMessageCard from "components/IconMessageCard";
import Alert from "components/Alert";
import Button from "components/Button";
import IconSpinner from "components/IconSpinner";
import IconTableHeader from "components/IconTableHeader";
import type { BeneficiaryInput } from "types/graphql";
import type { BeneficiaryWithAddressParts, RootState } from "types/state";

import "./Beneficiaries.scss";

const relationshipOptions = [
  "Spouse",
  "Child",
  "Mother",
  "Father",
  "Sister",
  "Brother",
  "Grandfather",
  "Grandmother",
  "Non-relative",
];

const beneficiaryTypes = [
  { label: "Primary", value: "PRIMARY" },
  { label: "Contingent", value: "CONTINGENT" },
];

/** A row of the beneficiary form: redux-formatted beneficiary or blank. */
interface BeneficiaryFormValues {
  familyName: string;
  givenName: string;
  city: string;
  country: string;
  postalCode: string;
  state: string;
  dateOfBirth: string;
  socialSecurityNumber: string;
  relationship: string;
  beneficiaryType: string;
  sharePercent?: number;
  address1?: string;
  address2?: string;
  streetAddress?: string[] | null;
}

const blankBeneficiary: BeneficiaryFormValues = {
  familyName: "",
  givenName: "",
  city: "",
  country: "USA",
  postalCode: "",
  state: "",
  dateOfBirth: "",
  socialSecurityNumber: "",
  relationship: "",
  beneficiaryType: "",
  sharePercent: undefined,
  address1: "",
  address2: "",
};

function testDob(this: yup.TestContext) {
  const dob = parseDob((this.parent as { dateOfBirth?: string }).dateOfBirth);
  const today = moment();
  const isBeforeToday = dob.startOf("day").isBefore(today.startOf("day"));
  const isLessThan100YearsOld = today.diff(dob, "years") < 100;

  return isBeforeToday && isLessThan100YearsOld;
}

function testSharePercent(this: yup.TestContext) {
  const allBeneficiaries = get(
    this.options,
    "from.1.value.beneficiaries",
    []
  ) as BeneficiaryFormValues[];
  let beneficiaryHasZeroSharePercent = false;
  const hasContingent = some(allBeneficiaries, {
    beneficiaryType: "CONTINGENT",
  });

  const { contingentSharePercent, primarySharePercent } = reduce(
    allBeneficiaries,
    (acc, beneficiary) => {
      const type: "contingentSharePercent" | "primarySharePercent" =
        beneficiary.beneficiaryType === "CONTINGENT"
          ? "contingentSharePercent"
          : "primarySharePercent";

      const sharePercent = beneficiary.sharePercent || 0;
      acc[type] += sharePercent;
      if (sharePercent === 0) {
        beneficiaryHasZeroSharePercent = true;
      }
      return acc;
    },
    { contingentSharePercent: 0, primarySharePercent: 0 }
  );

  const sharePercentsTotal100 = hasContingent
    ? contingentSharePercent + primarySharePercent === 200
    : primarySharePercent === 100;

  // should only pass if the sum of all beneficiaries is 100 and no beneficiary is 0 for share percent
  return !beneficiaryHasZeroSharePercent && sharePercentsTotal100;
}

const schema = yup.object().shape({
  beneficiaries: yup.array().of(
    yup.object().shape({
      givenName: yup
        .string()
        .label("First Name")
        .required()
        .min(1, "Must be at least one character.")
        .max(20),
      familyName: yup
        .string()
        .label("Last Name")
        .required()
        .min(1, "Must be at least one character.")
        .max(20),
      relationship: yup.string().label("Relationship").required(),
      beneficiaryType: yup.string().label("Beneficiary Type").required(),
      sharePercent: yup
        .number()
        .label("Share Percent")
        .test(
          "beneficiaries",
          "Share Percent must be greater than 0 and must total 100.",
          testSharePercent
        ),
      dateOfBirth: yup
        .string()
        .label("Date Of Birth")
        .required()
        .test("dob-format", DOB_FORMAT_ERROR, testDobFormat)
        .test(
          "dateOfBirth",
          "${path} must be before today and beneficiary cannot be over 100 years old.",
          testDob
        ),
      socialSecurityNumber: yup
        .string()
        .required()
        .label("Social Security Number")
        // explicit lambda: passing testSsn directly hands yup's TestContext
        // to the truthy allowNoDash parameter, silently loosening the format
        .test("socialSecurityNumber", INVALID_SSN_ERROR, (value) =>
          testSsn(value)
        ),
      address1: yup
        .string()
        .label("Address 1")
        .required()
        .min(2, "Must be at least two characters.")
        .max(30),
      address2: address2Validator,
      city: yup.string().label("City").required().max(50),
      country: yup.string().label("Country").required().max(50),
      state: stateValidator,
      postalCode: postalCodeValidator,
    })
  ),
});

const isFetchingSelector = createLoadingSelector([
  beneficiaryConstants.GET_BENEFICIARIES,
  accountConstants.GET_ACCOUNTS,
]);

const isUpdatingSelector = createLoadingSelector([
  beneficiaryConstants.UPDATE_BENEFICIARIES,
  beneficiaryConstants.DELETE_BENEFICIARIES,
]);

const errorSelector = createErrorSelector([
  beneficiaryConstants.UPDATE_BENEFICIARIES,
  beneficiaryConstants.DELETE_BENEFICIARIES,
]);

const mapStateToProps = (state: RootState) => {
  return {
    isFetching: isFetchingSelector(state),
    isUpdating: isUpdatingSelector(state),
    error: errorSelector(state),
    beneficiaries: state.beneficiary.beneficiaries,
    succesfullyUpdated: state.beneficiary.succesfullyUpdated,
    successfullyDeleted: state.beneficiary.successfullyDeleted,
    iraAccountId: getIraAccountIdSelector(state),
  };
};

const mapdispatchToProps = {
  getBeneficiaries,
  getAllAccounts,
  updateBeneficiaries,
  deleteBeneficiaries,
};

const connector = connect(mapStateToProps, mapdispatchToProps);

type Props = WithApolloClient<ConnectedProps<typeof connector>>;

interface State {
  beneficiaries: BeneficiaryFormValues[];
  isEditMode: boolean;
}

class Beneficiaries extends React.PureComponent<Props, State> {
  _promises: unknown[] = [];

  constructor(props: Props) {
    super(props);

    this.state = {
      beneficiaries: props.beneficiaries,
      isEditMode: true,
    };
  }

  static getDerivedStateFromProps(
    nextProps: Props,
    prevState: State
  ): Partial<State> {
    if (isEmpty(prevState.beneficiaries) && !isEmpty(nextProps.beneficiaries)) {
      return {
        beneficiaries: nextProps.beneficiaries,
        isEditMode: false,
      };
    }
    return {};
  }

  componentDidMount() {
    const acctPromise = this.props
      .getAllAccounts(this.props.client as ApolloClient<object>)
      .then(() => {
        const beneficiaryPromise = this.props.getBeneficiaries(
          this.props.client as ApolloClient<object>,
          this.props.iraAccountId as string
        );
        this._promises.push(beneficiaryPromise);
      });

    this._promises.push(acctPromise);
  }

  componentWillUnmount() {
    each(
      this._promises as Array<{ cancel?: () => void } | null | undefined>,
      (promise) => {
        if (promise && typeof promise.cancel === "function") {
          promise.cancel();
        }
      }
    );
  }

  _deleteBeneficiaries = () => {
    const deletePromise = this.props
      .deleteBeneficiaries(
        this.props.client as ApolloClient<object>,
        this.props.iraAccountId as string
      )
      .then((result) => {
        if (result.success) {
          toast.success("Your beneficiaries were successfully deleted.");
        } else {
          toast.error("There was a problem removing the beneficiaries.");
        }
      });
    this._promises.push(deletePromise);
  };

  _onSubmit = (values: State, { resetForm }: FormikHelpers<State>) => {
    // submit beneficiary form

    const beneficiaries = map(values.beneficiaries, (beneficiary) => {
      const { address1, address2, ...restOfProps } = beneficiary;

      return {
        ...restOfProps,
        streetAddress: [address1, address2],
      };
    });

    this._promises.push(
      this.props
        .updateBeneficiaries(
          this.props.client as ApolloClient<object>,
          {
            beneficiaries,
            accountId: this.props.iraAccountId,
          } as BeneficiaryInput
        )
        .then((result) => {
          if (!result.success) {
            return;
          }
          this.setState({
            isEditMode: false,
            beneficiaries: values.beneficiaries,
          });
          resetForm({});
        })
    );
  };

  _buildBeneficiaryRows = () => {
    return map(this.props.beneficiaries, (blankBeneficiary) => {
      return (
        <tr key={(blankBeneficiary as { id?: string }).id}>
          <td>
            {blankBeneficiary.givenName} {blankBeneficiary.familyName}
          </td>
          <td>{blankBeneficiary.beneficiaryType}</td>
          <td>{blankBeneficiary.sharePercent}%</td>
        </tr>
      );
    });
  };

  _buildReviewExistingColumns = (): Array<
    IconTableColumn<BeneficiaryWithAddressParts>
  > => {
    const columns: Array<IconTableColumn<BeneficiaryWithAddressParts>> = [
      {
        label: "Recipient",
        customComponent: (props: BeneficiaryWithAddressParts) => {
          return (
            <div>
              {props.givenName} {props.familyName}
            </div>
          );
        },
      },

      {
        label: "Beneficiary Type",
        key: "beneficiaryType",
      },

      {
        label: "Share Percent",
        customComponent: (props: BeneficiaryWithAddressParts) => {
          return <div>{props.sharePercent}%</div>;
        },
      },
    ];
    return columns;
  };

  render() {
    const columns = this._buildReviewExistingColumns();
    const data = this.props.beneficiaries;

    return (
      <div className="mega-container dashboard-content" id="beneficiaries">
        <section className="page-title-wrap">
          <article className="text-cell">
            <h1 className="page-title">Beneficiaries</h1>
            <p className="page-subtext">Manage your account beneficiaries</p>
            <div className="main-content">
              {this.props.isFetching && <IconSpinner centered />}
              {!this.props.isFetching && !this.state.isEditMode && (
                <>
                  <div className="beneficiary-view-card">
                    <Card>
                      <div className="beneficiaries-widget">
                        <IconTableHeader
                          tableHeader="Beneficiaries"
                          tableCount={size(this.props.beneficiaries)}
                        />
                        <Button
                          size="sm"
                          type="button"
                          color="action"
                          name="action"
                          btnLabel="Update Beneficiary"
                          onClick={() => {
                            this.setState({ isEditMode: true });
                          }}
                        />
                      </div>

                      <IconTable columns={columns} data={data} />
                    </Card>
                  </div>
                </>
              )}
              {!this.props.isFetching && this.state.isEditMode && (
                <Row>
                  <Col>
                    <Card className="beneficiary-form-card">
                      <Card.Body>
                        <Formik
                          validateOnChange={false}
                          validationSchema={schema}
                          onSubmit={this._onSubmit}
                          enableReinitialize={true}
                          initialValues={this.state}
                        >
                          {({
                            handleSubmit,
                            handleChange,
                            handleBlur,
                            values,
                            touched,
                            setFieldValue,
                            errors,
                          }) => (
                            <Form noValidate onSubmit={handleSubmit}>
                              <ScrollToFieldError />
                              {isEmpty(values.beneficiaries) &&
                                isEmpty(this.props.beneficiaries) && (
                                  <>
                                    <IconMessageCard
                                      header={
                                        "Would you like to add a beneficiary to your account?"
                                      }
                                      icon={
                                        <FiUsers
                                          color="white"
                                          stroke="#60A4BF"
                                          size={16}
                                        />
                                      }
                                    />
                                  </>
                                )}
                              {isEmpty(values.beneficiaries) &&
                                !isEmpty(this.props.beneficiaries) && (
                                  <div style={{ textAlign: "center" }}>
                                    <IconMessageCard
                                      header={
                                        "Press confirm deletion to confirm deletion of all beneficiaries."
                                      }
                                      icon={
                                        <FiUsers
                                          color="white"
                                          stroke="#60A4BF"
                                          size={16}
                                        />
                                      }
                                    />
                                  </div>
                                )}
                              <FieldArray
                                name="beneficiaries"
                                render={(
                                  arrayHelpers: FieldArrayRenderProps
                                ) => (
                                  <div>
                                    {values.beneficiaries &&
                                    values.beneficiaries.length > 0 ? (
                                      values.beneficiaries.map(
                                        (beneficiary, index) => (
                                          <div
                                            className="form-beneficiary"
                                            key={index}
                                          >
                                            <h4 className="beneficiary-index">
                                              Beneficiary #{index + 1}
                                            </h4>
                                            <section>
                                              <article
                                                className="col-form"
                                                style={{ paddingBottom: 0 }}
                                              >
                                                <h4 className="page-title">
                                                  Information
                                                </h4>
                                                <Row className="mb-3">
                                                  <Form.Group
                                                    as={Col}
                                                    md={6}
                                                    controlId={`formBasicbeneficiaries${index}GivenName`}
                                                  >
                                                    <Form.Label>
                                                      First Name
                                                    </Form.Label>
                                                    <Form.Control
                                                      name={`beneficiaries.${index}.givenName`}
                                                      placeholder="First Name"
                                                      value={getValue(
                                                        values,
                                                        `beneficiaries.${index}.givenName`
                                                      )}
                                                      onBlur={handleBlur}
                                                      onChange={handleChange}
                                                      isInvalid={isInvalid(
                                                        touched,
                                                        errors,
                                                        `beneficiaries.${index}.givenName`
                                                      )}
                                                      isValid={isValid(
                                                        touched,
                                                        errors,
                                                        `beneficiaries.${index}.givenName`
                                                      )}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                      {getError(
                                                        errors,
                                                        `beneficiaries.${index}.givenName`
                                                      )}
                                                    </Form.Control.Feedback>
                                                  </Form.Group>
                                                  <Form.Group
                                                    as={Col}
                                                    md={6}
                                                    controlId={`formBasicbeneficiaries${index}FamilyName`}
                                                  >
                                                    <Form.Label>
                                                      Last Name
                                                    </Form.Label>
                                                    <Form.Control
                                                      name={`beneficiaries.${index}.familyName`}
                                                      placeholder="Last Name"
                                                      value={getValue(
                                                        values,
                                                        `beneficiaries.${index}.familyName`
                                                      )}
                                                      onChange={handleChange}
                                                      isInvalid={isInvalid(
                                                        touched,
                                                        errors,
                                                        `beneficiaries.${index}.familyName`
                                                      )}
                                                      isValid={isValid(
                                                        touched,
                                                        errors,
                                                        `beneficiaries.${index}.familyName`
                                                      )}
                                                      onBlur={handleBlur}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                      {getError(
                                                        errors,
                                                        `beneficiaries.${index}.familyName`
                                                      )}
                                                    </Form.Control.Feedback>
                                                  </Form.Group>
                                                </Row>
                                                <Row className="mb-3">
                                                  <Form.Group
                                                    as={Col}
                                                    controlId={`formBasicbeneficiaries${index}DateOfBirth`}
                                                  >
                                                    <Form.Label>
                                                      Date of Birth
                                                    </Form.Label>
                                                    <Form.Control
                                                      as={MaskedInput}
                                                      mask="00/00/0000"
                                                      name={`beneficiaries.${index}.dateOfBirth`}
                                                      placeholder="MM/DD/YYYY"
                                                      value={getValue(
                                                        values,
                                                        `beneficiaries.${index}.dateOfBirth`
                                                      )}
                                                      onAccept={(
                                                        val: unknown,
                                                        mask: unknown
                                                      ) =>
                                                        handleChange({
                                                          target: {
                                                            name: (
                                                              mask as {
                                                                el: {
                                                                  input: HTMLInputElement;
                                                                };
                                                              }
                                                            ).el.input.name,
                                                            value: val,
                                                          },
                                                        } as unknown as React.ChangeEvent<HTMLInputElement>)
                                                      }
                                                      onBlur={handleBlur}
                                                      isInvalid={isInvalid(
                                                        touched,
                                                        errors,
                                                        `beneficiaries.${index}.dateOfBirth`
                                                      )}
                                                      isValid={isInvalid(
                                                        touched,
                                                        errors,
                                                        `beneficiaries.${index}.dateOfBirth`
                                                      )}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                      {getError(
                                                        errors,
                                                        `beneficiaries.${index}.dateOfBirth`
                                                      )}
                                                    </Form.Control.Feedback>
                                                  </Form.Group>
                                                  <Form.Group
                                                    as={Col}
                                                    controlId={`formBasicbeneficiaries${index}SsnField`}
                                                  >
                                                    <Form.Label>
                                                      Social Security Number
                                                    </Form.Label>
                                                    <Form.Control
                                                      as={MaskedInput}
                                                      mask="000-00-0000"
                                                      name={`beneficiaries.${index}.socialSecurityNumber`}
                                                      placeholder="111-11-1111"
                                                      value={getValue(
                                                        values,
                                                        `beneficiaries.${index}.socialSecurityNumber`
                                                      )}
                                                      onAccept={(
                                                        val: unknown,
                                                        mask: unknown
                                                      ) =>
                                                        handleChange({
                                                          target: {
                                                            name: (
                                                              mask as {
                                                                el: {
                                                                  input: HTMLInputElement;
                                                                };
                                                              }
                                                            ).el.input.name,
                                                            value: val,
                                                          },
                                                        } as unknown as React.ChangeEvent<HTMLInputElement>)
                                                      }
                                                      onBlur={handleBlur}
                                                      isInvalid={isInvalid(
                                                        touched,
                                                        errors,
                                                        `beneficiaries.${index}.socialSecurityNumber`
                                                      )}
                                                      isValid={isInvalid(
                                                        touched,
                                                        errors,
                                                        `beneficiaries.${index}.socialSecurityNumber`
                                                      )}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                      {getError(
                                                        errors,
                                                        `beneficiaries.${index}.socialSecurityNumber`
                                                      )}
                                                    </Form.Control.Feedback>
                                                  </Form.Group>
                                                </Row>
                                                <Row className="mb-3">
                                                  <Form.Group
                                                    as={Col}
                                                    md={4}
                                                    controlId={`formBasicbeneficiaries${index}Relationship`}
                                                  >
                                                    <Form.Label>
                                                      Relationship
                                                    </Form.Label>
                                                    <Form.Select
                                                      name={`beneficiaries.${index}.relationship`}
                                                      value={getValue(
                                                        values,
                                                        `beneficiaries.${index}.relationship`
                                                      )}
                                                      onBlur={handleBlur}
                                                      onChange={handleChange}
                                                      isInvalid={isInvalid(
                                                        touched,
                                                        errors,
                                                        `beneficiaries.${index}.relationship`
                                                      )}
                                                      isValid={isValid(
                                                        touched,
                                                        errors,
                                                        `beneficiaries.${index}.relationship`
                                                      )}
                                                    >
                                                      <option value="" disabled>
                                                        Select Relationship
                                                      </option>
                                                      {map(
                                                        relationshipOptions,
                                                        (option) => (
                                                          <option
                                                            key={option}
                                                            value={option}
                                                          >
                                                            {option}
                                                          </option>
                                                        )
                                                      )}
                                                    </Form.Select>
                                                    <Form.Control.Feedback type="invalid">
                                                      {getError(
                                                        errors,
                                                        `beneficiaries.${index}.relationship`
                                                      )}
                                                    </Form.Control.Feedback>
                                                  </Form.Group>

                                                  <Form.Group
                                                    as={Col}
                                                    md={4}
                                                    controlId={`formBasicbeneficiaries${index}BeneficiaryType`}
                                                  >
                                                    <Form.Label>
                                                      Beneficiary Type
                                                    </Form.Label>
                                                    <Form.Select
                                                      name={`beneficiaries.${index}.beneficiaryType`}
                                                      value={getValue(
                                                        values,
                                                        `beneficiaries.${index}.beneficiaryType`
                                                      )}
                                                      onBlur={handleBlur}
                                                      onChange={handleChange}
                                                      isInvalid={isInvalid(
                                                        touched,
                                                        errors,
                                                        `beneficiaries.${index}.beneficiaryType`
                                                      )}
                                                      isValid={isValid(
                                                        touched,
                                                        errors,
                                                        `beneficiaries.${index}.beneficiaryType`
                                                      )}
                                                    >
                                                      <option value="" disabled>
                                                        Select Beneficiary Type
                                                      </option>
                                                      {map(
                                                        beneficiaryTypes,
                                                        (type) => (
                                                          <option
                                                            key={type.value}
                                                            value={type.value}
                                                          >
                                                            {type.label}
                                                          </option>
                                                        )
                                                      )}
                                                    </Form.Select>

                                                    <Form.Control.Feedback type="invalid">
                                                      {getError(
                                                        errors,
                                                        `beneficiaries.${index}.beneficiaryType`
                                                      )}
                                                    </Form.Control.Feedback>
                                                  </Form.Group>

                                                  <Form.Group
                                                    as={Col}
                                                    md={4}
                                                    controlId={`formBasicbeneficiaries${index}SharePercent`}
                                                  >
                                                    <Form.Label>
                                                      Share Percent
                                                    </Form.Label>
                                                    <Form.Control
                                                      name={`beneficiaries.${index}.sharePercent`}
                                                      placeholder="Share Percent"
                                                      value={getValue<number>(
                                                        values,
                                                        `beneficiaries.${index}.sharePercent`
                                                      )}
                                                      onChange={handleChange}
                                                      onBlur={handleBlur}
                                                      isInvalid={isInvalid(
                                                        touched,
                                                        errors,
                                                        `beneficiaries.${index}.sharePercent`
                                                      )}
                                                      isValid={isValid(
                                                        touched,
                                                        errors,
                                                        `beneficiaries.${index}.sharePercent`
                                                      )}
                                                      type="number"
                                                      max={100}
                                                      min={0}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                      {getError(
                                                        errors,
                                                        `beneficiaries.${index}.sharePercent`
                                                      )}
                                                    </Form.Control.Feedback>
                                                  </Form.Group>
                                                </Row>

                                                <h4 className="page-title">
                                                  Address
                                                </h4>
                                                <Row className="mb-3">
                                                  <Form.Group
                                                    as={Col}
                                                    md={12}
                                                    controlId={`formBasicbeneficiaries${index}Adress1`}
                                                  >
                                                    <Form.Control
                                                      name={`beneficiaries.${index}.address1`}
                                                      placeholder="Address 1"
                                                      value={getValue(
                                                        values,
                                                        `beneficiaries.${index}.address1`
                                                      )}
                                                      onChange={handleChange}
                                                      isInvalid={isInvalid(
                                                        touched,
                                                        errors,
                                                        `beneficiaries.${index}.address1`
                                                      )}
                                                      isValid={isValid(
                                                        touched,
                                                        errors,
                                                        `beneficiaries.${index}.address1`
                                                      )}
                                                      onBlur={handleBlur}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                      {getError(
                                                        errors,
                                                        `beneficiaries.${index}.address1`
                                                      )}
                                                    </Form.Control.Feedback>
                                                  </Form.Group>
                                                </Row>
                                                <Row className="mb-3">
                                                  <Form.Group
                                                    as={Col}
                                                    md={12}
                                                    controlId={`formBasicbeneficiaries${index}Adress2`}
                                                  >
                                                    <Form.Control
                                                      name={`beneficiaries.${index}.address2`}
                                                      placeholder="Address 2"
                                                      value={getValue(
                                                        values,
                                                        `beneficiaries.${index}.address2`
                                                      )}
                                                      onBlur={handleBlur}
                                                      onChange={handleChange}
                                                      isInvalid={isInvalid(
                                                        touched,
                                                        errors,
                                                        `beneficiaries.${index}.address2`
                                                      )}
                                                      isValid={isValid(
                                                        touched,
                                                        errors,
                                                        `beneficiaries.${index}.address2`
                                                      )}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                      {getError(
                                                        errors,
                                                        `beneficiaries.${index}.address2`
                                                      )}
                                                    </Form.Control.Feedback>
                                                  </Form.Group>
                                                </Row>
                                                <Row className="mb-3">
                                                  <Form.Group
                                                    as={Col}
                                                    md={4}
                                                    controlId={`formBasicbeneficiaries${index}City`}
                                                  >
                                                    <Form.Control
                                                      name={`beneficiaries.${index}.city`}
                                                      placeholder="City"
                                                      value={getValue(
                                                        values,
                                                        `beneficiaries.${index}.city`
                                                      )}
                                                      onChange={handleChange}
                                                      onBlur={handleBlur}
                                                      isInvalid={isInvalid(
                                                        touched,
                                                        errors,
                                                        `beneficiaries.${index}.city`
                                                      )}
                                                      isValid={isValid(
                                                        touched,
                                                        errors,
                                                        `beneficiaries.${index}.city`
                                                      )}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                      {getError(
                                                        errors,
                                                        `beneficiaries.${index}.city`
                                                      )}
                                                    </Form.Control.Feedback>
                                                  </Form.Group>

                                                  <Form.Group
                                                    as={Col}
                                                    md={4}
                                                    controlId={`formBasicbeneficiaries${index}State`}
                                                  >
                                                    <Form.Select
                                                      name={`beneficiaries.${index}.state`}
                                                      value={getValue(
                                                        values,
                                                        `beneficiaries.${index}.state`
                                                      )}
                                                      onBlur={handleBlur}
                                                      onChange={handleChange}
                                                      isInvalid={isInvalid(
                                                        touched,
                                                        errors,
                                                        `beneficiaries.${index}.state`
                                                      )}
                                                      isValid={isValid(
                                                        touched,
                                                        errors,
                                                        `beneficiaries.${index}.state`
                                                      )}
                                                    >
                                                      <option value="" disabled>
                                                        Select State
                                                      </option>
                                                      {map(
                                                        states,
                                                        (
                                                          stateLabel,
                                                          stateAbbrv
                                                        ) => (
                                                          <option
                                                            key={stateLabel}
                                                            value={stateAbbrv}
                                                          >
                                                            {stateLabel}
                                                          </option>
                                                        )
                                                      )}
                                                    </Form.Select>

                                                    <Form.Control.Feedback type="invalid">
                                                      {getError(
                                                        errors,
                                                        `beneficiaries.${index}.state`
                                                      )}
                                                    </Form.Control.Feedback>
                                                  </Form.Group>

                                                  <Form.Group
                                                    as={Col}
                                                    md={4}
                                                    controlId={`formBasicbeneficiaries${index}PostalCode`}
                                                  >
                                                    <Form.Control
                                                      name={`beneficiaries.${index}.postalCode`}
                                                      placeholder="Zip"
                                                      value={getValue(
                                                        values,
                                                        `beneficiaries.${index}.postalCode`
                                                      )}
                                                      onChange={handleChange}
                                                      onBlur={handleBlur}
                                                      isInvalid={isInvalid(
                                                        touched,
                                                        errors,
                                                        `beneficiaries.${index}.postalCode`
                                                      )}
                                                      isValid={isValid(
                                                        touched,
                                                        errors,
                                                        `beneficiaries.${index}.postalCode`
                                                      )}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                      {getError(
                                                        errors,
                                                        `beneficiaries.${index}.postalCode`
                                                      )}
                                                    </Form.Control.Feedback>
                                                  </Form.Group>
                                                </Row>
                                              </article>
                                            </section>
                                            <div className="plus-minus-btns">
                                              <Button
                                                btnLabel=""
                                                size="sm"
                                                name="action"
                                                type="button"
                                                color="red"
                                                onClick={() =>
                                                  arrayHelpers.remove(index)
                                                }
                                                icon={{
                                                  icon: (
                                                    <FiMinus
                                                      size={14}
                                                      stroke={"Gray"}
                                                    />
                                                  ),
                                                  position: "left",
                                                }}
                                              />
                                              <Button
                                                btnLabel=""
                                                name="action"
                                                icon={{
                                                  icon: (
                                                    <FiPlus
                                                      size={14}
                                                      stroke={"#FFFFFF"}
                                                    />
                                                  ),
                                                  position: "left",
                                                }}
                                                size="sm"
                                                type="button"
                                                onClick={() =>
                                                  arrayHelpers.push({
                                                    ...blankBeneficiary,
                                                  })
                                                }
                                              />
                                            </div>
                                            {last(values.beneficiaries) ===
                                              beneficiary && (
                                              <div className="submit-row">
                                                {this.props.error && (
                                                  <Alert
                                                    type="error"
                                                    msg={this.props.error}
                                                  />
                                                )}
                                                <div className="buttons">
                                                  <Button
                                                    size="sm"
                                                    className="cancel-button"
                                                    type="button"
                                                    name="cancel"
                                                    color="cancel"
                                                    btnLabel="Cancel"
                                                    onClick={() => {
                                                      void (isEmpty(
                                                        this.props.beneficiaries
                                                      )
                                                        ? setFieldValue(
                                                            "beneficiaries",
                                                            []
                                                          )
                                                        : this.setState({
                                                            isEditMode: false,
                                                          }));
                                                    }}
                                                  />
                                                  {this.state.isEditMode &&
                                                    !this.props.isFetching && (
                                                      <div className="save-button">
                                                        <Button
                                                          size="sm"
                                                          name="submit"
                                                          withArrow={true}
                                                          btnLabel="Update Beneficiaries"
                                                          loading={
                                                            this.props
                                                              .isUpdating
                                                          }
                                                        />
                                                      </div>
                                                    )}
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        )
                                      )
                                    ) : (
                                      <div>
                                        {this.props.error && (
                                          <Alert
                                            type="error"
                                            msg={this.props.error}
                                          />
                                        )}

                                        <div className="buttons">
                                          {isEmpty(
                                            this.props.beneficiaries
                                          ) && (
                                            <Button
                                              withArrow={true}
                                              size="sm"
                                              name="action"
                                              btnLabel="Add a beneficiary"
                                              type="button"
                                              onClick={() =>
                                                arrayHelpers.push({
                                                  ...blankBeneficiary,
                                                })
                                              }
                                            />
                                          )}
                                          {!isEmpty(
                                            this.props.beneficiaries
                                          ) && (
                                            <>
                                              <span
                                                style={{
                                                  paddingRight: 10,
                                                }}
                                              >
                                                <Button
                                                  size="sm"
                                                  className="cancel-button"
                                                  type="button"
                                                  name="cancel"
                                                  color="cancel"
                                                  btnLabel="Cancel"
                                                  onClick={() => {
                                                    void (isEmpty(
                                                      this.props.beneficiaries
                                                    )
                                                      ? setFieldValue(
                                                          "beneficiaries",
                                                          []
                                                        )
                                                      : this.setState({
                                                          isEditMode: false,
                                                        }));
                                                  }}
                                                />
                                              </span>
                                              <Button
                                                withArrow={true}
                                                size="sm"
                                                color="red"
                                                name="action"
                                                btnLabel="Confirm Deletion"
                                                onClick={
                                                  this._deleteBeneficiaries
                                                }
                                                type="button"
                                                loading={this.props.isUpdating}
                                              />
                                            </>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              />
                            </Form>
                          )}
                        </Formik>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              )}
            </div>
          </article>
        </section>
      </div>
    );
  }
}

export default connector(withApollo<Props>(Beneficiaries));
